import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import config from "../../config.js";
import axios from "axios";
import Upload_Icon from "../../images/vehicle/Upload_icon.svg";
import TitleBar from "../navbar/TitleBar";
import NavMenu from "../navbar/NavMenu";
import { Country, State } from "country-state-city";
import RejectModal from "./RejectModal";
import { useTranslation } from "react-i18next";
import Edit_Icon from "../../images/table/Edit_Icon.svg";

axios.defaults.withCredentials = true;

function getApiErrorMessage(data) {
  if (!data || typeof data !== "object") return "";
  const e = data.error ?? data.message;
  if (typeof e === "string") return e;
  if (e && typeof e.message === "string") return e.message;
  if (Array.isArray(e)) return e.map(String).join(", ");
  return "";
}

export default function OrganizationUpdate() {
  const { id } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const { state: navigationState } = location;
  const currentPath = location.pathname;
  const navigate = useNavigate();

  // Refs for focus management
  const organizationNameRef = useRef(null);
  const domainRef = useRef(null);
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const cityRef = useRef(null);
  const addressRef = useRef(null);
  const pincodeRef = useRef(null);
  const contactNameRef = useRef(null);
  const contactEmailRef = useRef(null);
  const contactPhoneRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    domain: "",
    country: "",
    city: "",
    state: "",
    address: "",
    pincode: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    contactEmployeeId: "",
    contactDepartment: "",
    contactDesignation: "",
    userId: null,
    transportEmail: "",
    transportPhoneNumber: "",
    transportFullName: "",
    transportEmployeeId: "",
    joinCode: "",
  });

  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!id);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [successMessage, setSuccessMessage] = useState("");

  // Validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [backendErrors, setBackendErrors] = useState({
    name: "",
    domain: "",
    contactEmail: "",
    contactPhone: "",
    pincode: "",
    general: "",
  });

  // Country and state dropdown options
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Check if the current path is a request view
  const isRequestView = currentPath.startsWith("/request/view/");

  // Track approval status
  const [approvalStatus, setApprovalStatus] = useState("");

  // States for rejection dialog
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [orgNameToReject, setOrgNameToReject] = useState(
    navigationState?.selectedOrganization?.name || ""
  );

  // States for image upload
  const [organizationImage, setOrganizationImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploadError, setImageUploadError] = useState("");

  // Add this new state near other state declarations
  const [originalOrgName, setOriginalOrgName] = useState("");

  // Validation function
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value || value.trim() === "") {
          return "Organization name is required";
        }
        if (value.length < 2) {
          return "Organization name must be at least 2 characters";
        }
        return "";

      case "domain":
        if (!value || value.trim() === "") {
          return "Website domain is required";
        }

        const domainPattern =
          /^(https?:\/\/)?([A-Za-z0-9-]+\.)+[A-Za-z0-9-]+(\/.*)?$/;

        if (!domainPattern.test(value)) {
          return "Invalid domain format";
        }

        return "";

      case "country":
        if (!value || value.trim() === "") {
          return "Country is required";
        }
        return "";

      case "state":
        if (selectedCountry) {
          const countryStates = State.getStatesOfCountry(
            selectedCountry.isoCode
          );
          if (countryStates.length === 0) {
            return "";
          }
        }
        if (!value || value.trim() === "") {
          return "State is required";
        }
        return "";

      case "city":
        if (value && value.length < 2) {
          return "City must be at least 2 characters";
        }
        return "";

      case "address":
        if (!value || value.trim() === "") {
          return "Address is required";
        }
        if (value.length < 5) {
          return "Address must be at least 5 characters";
        }
        return "";

      case "pincode":
        if (!value || value.trim() === "") {
          return "Pincode is required";
        }
        const pincodePattern = /^\d{6}$/;
        if (!pincodePattern.test(value)) {
          return "Pincode must be 6 digits";
        }
        return "";

      case "contactName":
        if (!value || value.trim() === "") {
          return "Contact name is required";
        }
        if (value.length < 2) {
          return "Contact name must be at least 2 characters";
        }
        return "";

      case "contactEmail":
        if (!value || value.trim() === "") {
          return "Contact email is required";
        }
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) {
          return "Please enter a valid email address";
        }
        return "";

      case "contactPhone":
        if (!value || value.trim() === "") {
          return "Contact phone is required";
        }
        const phonePattern = /^\d{10}$/;
        if (!phonePattern.test(value.replace(/\D/g, ""))) {
          return "Phone number must be 10 digits";
        }
        return "";

      case "contactEmployeeId":
        if (value && value.length < 2) {
          return "Employee ID must be at least 2 characters";
        }
        return "";

      case "contactDepartment":
        if (value && value.length < 2) {
          return "Department must be at least 2 characters";
        }
        return "";

      case "contactDesignation":
        if (value && value.length < 2) {
          return "Designation must be at least 2 characters";
        }
        return "";

      default:
        return "";
    }
  };

  // Load countries on component mount
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Update states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(countryStates);

      // If country has no states, automatically set state to "N/A"
      if (countryStates.length === 0) {
        setFormData((prev) => ({ ...prev, state: "N/A" }));
      } else {
        // Clear state if country has states but current state is "N/A"
        if (formData.state === "N/A") {
          setFormData((prev) => ({ ...prev, state: "" }));
        }
      }
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    const fetchUserAndOrgData = async () => {
      const storedUserData = localStorage.getItem("userState");
      if (!storedUserData) {
        setNotification({
          open: true,
          message: "User not authenticated. Please log in.",
          severity: "error",
        });
        navigate("/login");
        return;
      }

      const parsedUserData = JSON.parse(storedUserData);
      const userId = parsedUserData.user.id;
      setFormData((prev) => ({ ...prev, userId }));

      // // Check if organization data was passed in navigation state
      // if (navigationState && navigationState.selectedOrganization) {
      //     console.log('Using organization data from navigation state:', navigationState.selectedOrganization);

      //     const organizationData = navigationState.selectedOrganization;

      //     // Find the country object based on name
      //     const countryObj = Country.getAllCountries().find(
      //         country => country.name === organizationData.country
      //     );

      //     if (countryObj) {
      //         setSelectedCountry(countryObj);
      //     }

      //     // Set approval status
      //     if (organizationData.approvalStatus) {
      //         setApprovalStatus(organizationData.approvalStatus);
      //     }

      //     // Check for organization logo in documents
      //     if (organizationData.documents && Array.isArray(organizationData.documents)) {
      //         const organizationDoc = organizationData.documents.find(
      //             (doc) => doc.name === "Organization" && doc.location
      //         );
      //         if (organizationDoc && organizationDoc.location) {
      //             try {
      //                 const imageUrl = `${config.baseURL}/upload/image?fileName=${organizationDoc.location}`;
      //                 const imageBlobUrl = await fetchImage(imageUrl);
      //                 if (imageBlobUrl) {
      //                     setImagePreview(imageBlobUrl);
      //                 }
      //             } catch (e) {
      //                 console.error("Error fetching organization logo:", e);
      //             }
      //         }
      //     }

      //     setFormData({ ...organizationData, userId });
      //     setLoading(false);
      //     return;
      // }

      // // If no data in navigation state, fetch from API
      // if (id) {
      //     try {
      //         setLoading(true);
      //         const response = await axios.get(`${config.baseURL}/organization/${id}`);
      //         const organizationData = response.data.organization || response.data;

      //         console.log('Fetched organization data from API:', organizationData);

      //         // Find the country object based on name
      //         const countryObj = Country.getAllCountries().find(
      //             country => country.name === organizationData.country
      //         );

      //         if (countryObj) {
      //             setSelectedCountry(countryObj);
      //         }

      //         // Set approval status if it exists
      //         if (organizationData.approvalStatus) {
      //             setApprovalStatus(organizationData.approvalStatus);
      //         }

      //         // Check for organization logo in documents
      //         if (organizationData.documents && Array.isArray(organizationData.documents)) {
      //             const organizationDoc = organizationData.documents.find(
      //                 (doc) => doc.name === "Organization" && doc.location
      //             );
      //             if (organizationDoc && organizationDoc.location) {
      //                 try {
      //                     const imageUrl = `${config.baseURL}/upload/image?fileName=${organizationDoc.location}`;
      //                     const imageBlobUrl = await fetchImage(imageUrl);
      //                     if (imageBlobUrl) {
      //                         setImagePreview(imageBlobUrl);
      //                     }
      //                 } catch (e) {
      //                     console.error("Error fetching organization logo:", e);
      //                 }
      //             }
      //         }

      //         setFormData({ ...organizationData, userId });
      //     } catch (error) {
      //         console.error('Error fetching organization data:', error);
      //         setNotification({
      //             open: true,
      //             message: error.response?.data?.message || 'Failed to load organization data',
      //             severity: 'error',
      //         });
      //         if (error.response?.status === 401 || error.response?.status === 403) {
      //             navigate('/login');
      //         }
      //     } finally {
      //         setLoading(false);
      //     }
      // }

      // Get organization ID from navigation state or URL params
      const orgId = id;

      if (orgId) {
        try {
          setLoading(true);
          const response = await axios.get(
            `${config.baseURL}/organization/${orgId}`
          );
          const organizationData = response.data.organization || response.data;

          console.log("Fetched organization data from API:", organizationData);
          setOriginalOrgName(organizationData.name);
          // Find the country object based on name
          const countryObj = Country.getAllCountries().find(
            (country) => country.name === organizationData.country
          );

          if (countryObj) {
            setSelectedCountry(countryObj);
          }

          // Set approval status if it exists
          if (organizationData.approvalStatus) {
            setApprovalStatus(organizationData.approvalStatus);
          }

          // Check for organization logo in documents
          if (
            organizationData.documents &&
            Array.isArray(organizationData.documents)
          ) {
            const organizationDoc = organizationData.documents.find(
              (doc) => doc.name === "Organization" && doc.location
            );
            if (organizationDoc && organizationDoc.location) {
              try {
                const imageUrl = `${config.baseURL}/upload/image?fileName=${organizationDoc.location}`;
                const imageBlobUrl = await fetchImage(imageUrl);
                if (imageBlobUrl) {
                  setImagePreview(imageBlobUrl);
                }
              } catch (e) {
                console.error("Error fetching organization logo:", e);
              }
            }
          }

          setFormData({ ...organizationData, userId });
        } catch (error) {
          console.error("Error fetching organization data:", error);
          setNotification({
            open: true,
            message:
              error.response?.data?.message ||
              "Failed to load organization data",
            severity: "error",
          });
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            navigate("/login");
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserAndOrgData();
  }, [id, navigate, navigationState]);

  // Fetch image for preview
  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for country selection
    if (name === "country") {
      const countryObj = countries.find((country) => country.name === value);
      setSelectedCountry(countryObj);
      // Clear state when country changes
      setFormData((prev) => ({ ...prev, [name]: value, state: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear backend errors when user starts typing
    if (backendErrors[name]) {
      setBackendErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Validate on change
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const uploadOrganizationLogo = async (organizationId) => {
    if (!organizationImage) return null;

    const imageFormData = new FormData();
    imageFormData.append("image", organizationImage);

    try {
      // ADD THIS: Use different endpoint for request view
      const uploadEndpoint = isRequestView
        ? `${config.baseURL}/upload/image?name=Organization&associateEntityId=${organizationId}&associatedWith=OrganizationLogo&isRequest=true`
        : `${config.baseURL}/upload/image?name=Organization&associateEntityId=${organizationId}&associatedWith=OrganizationLogo`;

      const response = await axios.post(
        uploadEndpoint, // CHANGED: use uploadEndpoint instead of hardcoded URL
        imageFormData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageUploadError(
        "Failed to upload organization logo. Please try again."
      );
      setNotification({
        open: true,
        message: "Failed to upload organization logo",
        severity: "error",
      });
      throw error;
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    if (error) {
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset all errors
    setErrors({});
    setBackendErrors({
      name: "",
      domain: "",
      contactEmail: "",
      contactPhone: "",
      pincode: "",
      general: "",
    });

    // Validate all required fields
    const requiredFields = [
      "name",
      "domain",
      "country",
      "state",
      "address",
      "pincode",
      "contactName",
      "contactEmail",
      "contactPhone",
    ];

    let hasErrors = false;
    const newErrors = {};
    const newTouched = {};

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        newTouched[field] = true;
        hasErrors = true;
      }
    });

    // Validate optional fields that have values
    Object.keys(formData).forEach((field) => {
      if (!requiredFields.includes(field) && formData[field]) {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          newTouched[field] = true;
          hasErrors = true;
        }
      }
    });

    setErrors(newErrors);
    setTouched(newTouched);

    if (hasErrors) {
      setNotification({
        open: true,
        message: "Please fix all validation errors before submitting",
        severity: "error",
      });

      // Focus on first field with error
      const fieldOrder = [
        "name",
        "domain",
        "country",
        "state",
        "city",
        "address",
        "pincode",
        "contactName",
        "contactEmail",
        "contactPhone",
      ];

      for (const field of fieldOrder) {
        if (newErrors[field]) {
          const refMap = {
            name: organizationNameRef,
            domain: domainRef,
            country: countryRef,
            state: stateRef,
            city: cityRef,
            address: addressRef,
            pincode: pincodeRef,
            contactName: contactNameRef,
            contactEmail: contactEmailRef,
            contactPhone: contactPhoneRef,
          };

          if (refMap[field] && refMap[field].current) {
            refMap[field].current.focus();
            break;
          }
        }
      }
      return;
    }

    setLoading(true);

    try {
      // let response;
      // if (isEditMode) {
      //     response = await axios.put(
      //         `${config.baseURL}/organization/${id}`,
      //         formData
      //     );
      // } else {
      //     response = await axios.post(
      //         `${config.baseURL}/organization/admin`,
      //         formData
      //     );
      // }

      const dataToSend = {
        ...formData,
        city: formData.city || "",
        contactEmployeeId: formData.contactEmployeeId || "",
        contactDepartment: formData.contactDepartment || "",
        contactDesignation: formData.contactDesignation || "",
        transportFullName: formData.transportFullName || "",
        transportEmail: formData.transportEmail || "",
        transportPhoneNumber: formData.transportPhoneNumber || "",
        transportEmployeeId: formData.transportEmployeeId || "",
      };

      let response;
      if (isEditMode) {
        const endpoint = isRequestView
          ? `${config.baseURL}/register/new-request/${id}`
          : `${config.baseURL}/organization/${id}`;

        response = await axios.put(endpoint, dataToSend);
        console.log("Update response:", response.data); // Debug log
      } else {
        response = await axios.post(
          `${config.baseURL}/organization/admin`,
          dataToSend
        );
        console.log("Create response:", response.data); // Debug log
      }

      // Upload organization logo if selected
      let imageResponse = null;
      if (organizationImage) {
        // Get organization ID from response or use the URL param ID
        const orgId =
          response.data.organization?.id ||
          response.data?.id ||
          response.data?.data?.id ||
          id;

        console.log("Uploading image for org ID:", orgId); // Debug log

        try {
          imageResponse = await uploadOrganizationLogo(orgId);
          console.log("Image upload successful:", imageResponse); // Debug log
        } catch (imageError) {
          console.error("Image upload failed:", imageError);
          // Continue with success message even if image fails
          // The error notification is already handled in uploadOrganizationLogo
        }
      }

      // Set success message
      const message = isEditMode
        ? "Organization updated successfully"
        : "Organization created successfully";

      setSuccessMessage(message);

      // Clear success message and navigate after 2 seconds
      setTimeout(() => {
        setSuccessMessage("");
        navigate("/org");
      }, 2000);
    } catch (error) {
      console.error("Error saving organization:", error);

      const topLevelMsg = getApiErrorMessage(error.response?.data);
      if (topLevelMsg?.includes("already exists")) {
        const errMsg = topLevelMsg;
        const cleanMsg = errMsg
          .replace("Failed to create organization:", "")
          .replace("Failed to update organization:", "")
          .trim();

        setBackendErrors((prev) => ({
          ...prev,
          general: cleanMsg || "Domain or email already in use.",
        }));
        setLoading(false);
        return;
      }

      setBackendErrors({
        name: "",
        domain: "",
        contactEmail: "",
        contactPhone: "",
        pincode: "",
        general: "",
      });
      const errorMessage = getApiErrorMessage(error.response?.data);

      if (errorMessage) {
        if (errorMessage.includes("Validation failed:")) {
          const validationErrors = errorMessage
            .split("Validation failed:")[1]
            .split(",")
            .map((err) => err.trim());

          validationErrors.forEach((error) => {
            if (error.includes("name already exists")) {
              setBackendErrors((prev) => ({
                ...prev,
                name: "Organization name already exists",
              }));
            }
            if (error.includes("domain already registered")) {
              setBackendErrors((prev) => ({
                ...prev,
                domain: "Domain already registered",
              }));
            }
            if (error.includes("email already registered")) {
              setBackendErrors((prev) => ({
                ...prev,
                contactEmail: "Email already registered",
              }));
            }
            if (error.includes("Invalid domain format")) {
              setBackendErrors((prev) => ({
                ...prev,
                domain: "Invalid domain format",
              }));
            }
            if (error.includes("Invalid pincode format")) {
              setBackendErrors((prev) => ({
                ...prev,
                pincode: "Pincode must be 6 digits",
              }));
            }
            // Add these new transport error cases
            if (error.includes("Invalid transport email format")) {
              setBackendErrors((prev) => ({
                ...prev,
                transportEmail: "Invalid transport email format",
              }));
            }
            if (error.includes("Invalid transport phone number format")) {
              setBackendErrors((prev) => ({
                ...prev,
                transportPhoneNumber: "Invalid transport phone number format",
              }));
            }
          });

          const otherErrors = validationErrors
            .filter(
              (err) =>
                !err.includes("name already exists") &&
                !err.includes("domain already registered") &&
                !err.includes("email already registered") &&
                !err.includes("Invalid domain format") &&
                !err.includes("Invalid pincode format")
            )
            .join(", ");

          if (otherErrors) {
            setBackendErrors((prev) => ({
              ...prev,
              general: otherErrors,
            }));
            setNotification({
              open: true,
              message: otherErrors,
              severity: "error",
            });
          }
        } else {
          setBackendErrors((prev) => ({
            ...prev,
            general: errorMessage,
          }));
          setNotification({
            open: true,
            message: errorMessage,
            severity: "error",
          });
        }
      } else {
        const fallbackMsg =
          error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} organization`;
        setBackendErrors((prev) => ({
          ...prev,
          general: fallbackMsg,
        }));
        setNotification({
          open: true,
          message: fallbackMsg,
          severity: "error",
        });
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/png", "image/jpeg", "image/gif"];
      if (!validTypes.includes(file.type)) {
        setImageUploadError("Please upload a PNG, JPG, or GIF image");
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setImageUploadError("Image size must be less than 10MB");
        return;
      }

      setOrganizationImage(file);
      setImageUploadError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    const storedUserData = localStorage.getItem("userState");
    const userId = storedUserData ? JSON.parse(storedUserData).user.id : null;

    if (isEditMode) {
      setFormData((prev) => ({ ...prev, userId }));
      navigate(-1);
    } else {
      setFormData({
        name: "",
        domain: "",
        country: "",
        city: "",
        state: "",
        address: "",
        pincode: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        contactEmployeeId: "",
        contactDepartment: "",
        contactDesignation: "",
        userId,
      });
      navigate(-1);
    }
  };

  const handleAssign = () => {
    // Create a navigation state object with the organization data
    const organizationData = {
      ...formData,
      approvalStatus, // Include approval status
      id: id,
    };
    console.log(id, "hahahah");
    const requestId = navigationState?.selectedOrganization?.id || id;
    // Determine the correct path based on the current path
    const assignPath = isRequestView
      ? `/request/feature/${id}`
      : `/organization/feature/${id}`;

    // Navigate with state data
    navigate(assignPath, {
      state: {
        selectedOrganization: organizationData,
        requestId: requestId,
      },
    });
  };

  const handleReject = (id) => {
    // Open the rejection modal
    setRejectModalOpen(true);
  };

  const confirmReject = async () => {
    setIsActionLoading(true);

    try {
      const orgId = navigationState.selectedOrganization.id;
      console.log("orgId", orgId);
      await axios.post(
        `${config.baseURL}/register/${orgId}/reject`,
        { reason: rejectionReason },
        { withCredentials: true }
      );
      setRejectModalOpen(false);
      setNotification({
        open: true,
        message: "Organization rejected successfully",
        severity: "success",
      });
      navigate("/admin");
    } catch (error) {
      console.error("Error rejecting organization:", error);
      setNotification({
        open: true,
        message: error.response?.data?.msg || "Failed to reject organization",
        severity: "error",
      });
      setRejectModalOpen(false);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleAccept = async () => {
    setIsActionLoading(true);
    try {
      const orgId = navigationState.selectedOrganization.id;
      await axios.post(
        `${config.baseURL}/register/${orgId}/accept`,
        {},
        { withCredentials: true }
      );
      setNotification({
        open: true,
        message: "Organization approved successfully",
        severity: "success",
      });
      navigate("/org");
    } catch (error) {
      console.error("Error approving organization:", error);
      setNotification({
        open: true,
        message: error.response?.data?.msg || "Failed to approve organization",
        severity: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSave = async () => {
    setIsActionLoading(true);
    try {
      const requestId = navigationState?.selectedOrganization?.id || id;

      await axios.put(
        `${config.baseURL}/register/new-request/${requestId}`,
        {
          ...formData,
          city: formData.city || "",
          contactEmployeeId: formData.contactEmployeeId || "",
          contactDepartment: formData.contactDepartment || "",
          contactDesignation: formData.contactDesignation || "",
          transportFullName: formData.transportFullName || "",
          transportEmail: formData.transportEmail || "",
          transportPhoneNumber: formData.transportPhoneNumber || "",
          transportEmployeeId: formData.transportEmployeeId || "",
        },
        { withCredentials: true }
      );

      // ONLY for image upload, use the actual organization ID (171)
      if (organizationImage) {
        try {
          const actualOrgId = formData.id; // This is 171 from the formData
          console.log("Uploading image for actual org ID:", actualOrgId);
          await uploadOrganizationLogo(actualOrgId);
          console.log("Image uploaded successfully during save");
        } catch (imageError) {
          console.error("Image upload failed during save:", imageError);
        }
      }

      setNotification({
        open: true,
        message: "Organization details saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving organization details:", error);
      setNotification({
        open: true,
        message:
          error.response?.data?.msg || "Failed to save organization details",
        severity: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Helper function to get field error styles
  const getFieldStyles = (fieldName) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      height: "55px",
      "& fieldset": {
        border:
          touched[fieldName] && (errors[fieldName] || backendErrors[fieldName])
            ? "2px solid #EE3B2B"
            : "2px solid #183059",
      },
      "&:hover fieldset": {
        border:
          touched[fieldName] && (errors[fieldName] || backendErrors[fieldName])
            ? "2px solid #EE3B2B"
            : "2px solid #183059",
      },
      "&.Mui-focused fieldset": {
        border:
          touched[fieldName] && (errors[fieldName] || backendErrors[fieldName])
            ? "2px solid #EE3B2B"
            : "2px solid #183059",
      },
    },
  });

  // Helper function to render error message
  const renderFieldError = (fieldName) => {
    const error = errors[fieldName] || backendErrors[fieldName];
    if (touched[fieldName] && error) {
      return (
        <Typography
          variant="caption"
          sx={{
            color: "#EE3B2B",
            fontSize: "0.75rem",
            marginTop: "4px",
            marginLeft: "14px",
            display: "block",
          }}
        >
          {error}
        </Typography>
      );
    }
    return null;
  };

  // Determine title, path, and action buttons based on the current URL
  let title, path, actionButtons;

  if (isRequestView) {
    title = "Organization";
    path = "Organization > New Request > View Details";

    // Different buttons based on approval status
    if (approvalStatus === "REJECTED") {
      actionButtons = (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#F2F2F3",
              border: "1px solid #414958",
              color: "#1F242E",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#F9E719",
              border: "1px solid #414958",
              color: "#111214",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
              ":hover": { background: "#F9E719" },
            }}
          >
            {isActionLoading ? <CircularProgress size={24} /> : "Save"}
          </Button>
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#F9E719",
              border: "1px solid #414958",
              color: "#111214",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
              ":hover": { background: "#F9E719" },
            }}
          >
            {isActionLoading ? <CircularProgress size={24} /> : "Accept"}
          </Button>
        </Box>
      );
    } else {
      actionButtons = (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleReject}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#EE3B2B",
              border: "1px solid #414958",
              color: "#FFFFFF",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
              ":hover": { background: "#EE3B2B" },
            }}
          >
            {isActionLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Reject"
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#F2F2F3",
              border: "1px solid #414958",
              color: "#1F242E",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#F9E719",
              border: "1px solid #414958",
              color: "#111214",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
              ":hover": { background: "#F9E719" },
            }}
          >
            {isActionLoading ? <CircularProgress size={24} /> : "Save"}
          </Button>
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: 55,
              borderRadius: "8px",
              background: "#F9E719",
              border: "1px solid #414958",
              color: "#111214",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
              textTransform: "none",
              ":hover": { background: "#F9E719" },
            }}
          >
            {isActionLoading ? <CircularProgress size={24} /> : "Accept"}
          </Button>
        </Box>
      );
    }
  } else {
    const orgSubtitleName = JSON.parse(localStorage.getItem("userState"))
      ?.organization?.name;
    title = isEditMode ? "Update Organization" : "Create Organizations";
    path = isEditMode
      ? `${
          orgSubtitleName || "organization"
        } > Organizations > ${originalOrgName} `
      : `${orgSubtitleName || "organization"} > Organizations > Create`;
    actionButtons = (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            width: 122,
            height: 55,
            borderRadius: "8px",
            background: "#F2F2F3",
            border: "1px solid #414958",
            color: "#1F242E",
            fontFamily: "Roboto",
            fontWeight: 600,
            fontSize: "20px",
            textTransform: "none",
            ":hover": { background: "#F2F2F3" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          sx={{
            width: 125,
            height: 55,
            borderRadius: "8px",
            background: "#F9E719",
            border: "1px solid #414958",
            color: "#1F242E",
            fontFamily: "Roboto",
            fontWeight: 600,
            fontSize: "20px",
            textTransform: "none",
            ":hover": { background: "#F9E719" },
          }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : isEditMode ? (
            "Update"
          ) : (
            "Submit"
          )}
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title={title}
          path={path}
          track={isEditMode && isRequestView ? "Requested features" : ""}
          onTrackClick={handleAssign}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
            height: "100vh",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading...</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <TitleBar />
      <NavMenu />
      {approvalStatus === "REJECTED" && (
        <Box
          sx={{
            backgroundColor: "#EE3B2B",
            color: "#721C24",
            height: "36px",
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            fontFamily: "Roboto",
            fontWeight: 600,
            fontSize: "24px",
            color: "#FFFFFF",
          }}
        >
          Application Rejected
        </Box>
      )}
      <SubTitle
        title={title}
        path={path}
        track={isEditMode || isRequestView ? "Requested features" : ""}
        onTrackClick={handleAssign}
      />
      <Box sx={{ margin: "0px 40px" }}>
        <Paper
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            padding: "20px",
            border: " 1px solid #A5DCF3",
          }}
        >
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 1 }}
          >
            {/* Display backend general errors */}
            {backendErrors.general && (
              <Box
                sx={{
                  backgroundColor: "#FDE7E7",
                  color: "#D32F2F",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <Typography>{backendErrors.general}</Typography>
              </Box>
            )}

            <Box>
              <Box
                sx={{
                  bgcolor: "#647490",
                  color: "white",
                  p: 2,
                  borderRadius: "8px",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                }}
              >
                Organization Details
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Organization Name{" "}
                      <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={t("Enter Organization Name (e.g.KINO)")}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      inputRef={organizationNameRef}
                      error={
                        touched.name && (!!errors.name || !!backendErrors.name)
                      }
                      sx={getFieldStyles("name")}
                    />
                    {renderFieldError("name")}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Website <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="domain"
                      value={formData.domain}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter Website (e.g. example.com)"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      inputRef={domainRef}
                      disabled={isEditMode}
                      error={
                        touched.domain &&
                        (!!errors.domain || !!backendErrors.domain)
                      }
                      sx={getFieldStyles("domain")}
                    />
                    {renderFieldError("domain")}
                  </Grid>
                  {isEditMode && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Driver Join Code
                      </Typography>
                      <TextField
                        fullWidth
                        name="joinCode"
                        value={formData.joinCode || ""}
                        placeholder="Not set"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ readOnly: true }}
                        sx={getFieldStyles("joinCode")}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#647490", mt: 0.5, display: "block" }}
                      >
                        Share this code with drivers for self-registration.
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Country <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      required
                      fullWidth
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      variant="outlined"
                      displayEmpty
                      InputLabelProps={{ shrink: true }}
                      inputRef={countryRef}
                      error={touched.country && !!errors.country}
                      sx={getFieldStyles("country")}
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: {
                          PaperProps: {
                            style: {
                              maxHeight: 250,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <span style={{ color: "#999" }}>Select Country</span>
                      </MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.isoCode} value={country.name}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    {renderFieldError("country")}
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          City
                        </Typography>
                        <TextField
                          fullWidth
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder={t("Enter City (e.g.Bengaluru)")}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          inputRef={cityRef}
                          error={touched.city && !!errors.city}
                          sx={getFieldStyles("city")}
                        />
                        {renderFieldError("city")}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          State <span style={{ color: "#EE3B2B" }}>*</span>
                        </Typography>
                        <TextField
                          select
                          required
                          fullWidth
                          name="state"
                          displayEmpty
                          value={formData.state}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          disabled={!selectedCountry}
                          inputRef={stateRef}
                          error={touched.state && !!errors.state}
                          sx={getFieldStyles("state")}
                          SelectProps={{
                            native: false,
                            displayEmpty: true,
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 250,
                                  overflowY: "auto",
                                },
                              },
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              disableAutoFocusItem: false,
                            },
                          }}
                        >
                          {!selectedCountry ? (
                            <MenuItem key="placeholder" value="" disabled>
                              <span style={{ color: "#999" }}>
                                Select State
                              </span>
                            </MenuItem>
                          ) : states.length === 0 ? (
                            <MenuItem value="N/A">
                              N/A (No states available)
                            </MenuItem>
                          ) : (
                            [
                              <MenuItem key="placeholder" value="" disabled>
                                <span style={{ color: "#999" }}>
                                  Select State
                                </span>
                              </MenuItem>,
                              ...states.map((state) => (
                                <MenuItem
                                  key={state.isoCode}
                                  value={state.name}
                                  onClick={() => {
                                    handleInputChange({
                                      target: {
                                        name: "state",
                                        value: state.name,
                                      },
                                    });
                                  }}
                                >
                                  {state.name}
                                </MenuItem>
                              )),
                            ]
                          )}
                        </TextField>
                        {renderFieldError("state")}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Address <span style={{ color: "#EE3B2B" }}>*</span>
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder={t("Enter Address (e.g.123 MG Road)")}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          inputRef={addressRef}
                          error={touched.address && !!errors.address}
                          sx={getFieldStyles("address")}
                        />
                        {renderFieldError("address")}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          PIN Code <span style={{ color: "#EE3B2B" }}>*</span>
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder={t("Enter Pincode (e.g.560001)")}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          inputRef={pincodeRef}
                          error={
                            touched.pincode &&
                            (!!errors.pincode || !!backendErrors.pincode)
                          }
                          sx={getFieldStyles("pincode")}
                        />
                        {renderFieldError("pincode")}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        borderRadius: "8px",
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        border: imagePreview
                          ? "1px solid #201E46"
                          : "2px dashed #ccc",
                        backgroundColor: imagePreview
                          ? "transparent"
                          : "#f8f9fa",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                          ...(imagePreview ? { height: "150px" } : {}),
                        }}
                      >
                        {imagePreview ? (
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              background: "#f8f9fa",
                            }}
                          >
                            <img
                              src={imagePreview}
                              alt="Organization Logo"
                              style={{
                                width: "80%",
                                height: "80%",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: "0px",
                                right: "0px",
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              }}
                            >
                              <img src={Edit_Icon} alt="edit" />
                            </div>
                          </div>
                        ) : (
                          <img
                            src={Upload_Icon}
                            alt="Upload"
                            style={{ width: 40, height: 40 }}
                          />
                        )}
                      </Box>
                      {!imagePreview && (
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#1F242E",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: "16px",
                              lineHeight: "100%",
                              mb: 1,
                            }}
                          >
                            {t("Organization Logo")}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            {t("Click to upload or drag and drop")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", fontSize: "12px" }}
                          >
                            {t("PNG, JPG, GIF up to 10MB")}
                          </Typography>
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          cursor: "pointer",
                          zIndex: 1,
                        }}
                      />
                      {imageUploadError && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#EE3B2B",
                            fontSize: "0.75rem",
                            marginTop: "8px",
                            textAlign: "center",
                          }}
                        >
                          {imageUploadError}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  bgcolor: "#647490",
                  color: "white",
                  p: 2,
                  borderRadius: "8px",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                }}
              >
                Contact Details
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Full Name <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={t("Enter Full Name (e.g.Rahul Sharma)")}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      inputRef={contactNameRef}
                      error={touched.contactName && !!errors.contactName}
                      sx={getFieldStyles("contactName")}
                    />
                    {renderFieldError("contactName")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Email <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={t("Enter Email (e.g.rahul@example.com)")}
                      type="email"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputRef={contactEmailRef}
                      error={
                        touched.contactEmail &&
                        (!!errors.contactEmail || !!backendErrors.contactEmail)
                      }
                      sx={getFieldStyles("contactEmail")}
                    />
                    {renderFieldError("contactEmail")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Phone Number <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={t("Enter Phone Number (e.g.9876543210)")}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="tel"
                      inputRef={contactPhoneRef}
                      error={
                        touched.contactPhone &&
                        (!!errors.contactPhone || !!backendErrors.contactPhone)
                      }
                      sx={getFieldStyles("contactPhone")}
                    />
                    {renderFieldError("contactPhone")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Employee ID
                    </Typography>
                    <TextField
                      fullWidth
                      name="contactEmployeeId"
                      value={formData.contactEmployeeId || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={t("Enter Employee ID (e.g.EMP1234)")}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      error={
                        touched.contactEmployeeId && !!errors.contactEmployeeId
                      }
                      sx={getFieldStyles("contactEmployeeId")}
                    />
                    {renderFieldError("contactEmployeeId")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Department
                    </Typography>
                    <TextField
                      fullWidth
                      name="contactDepartment"
                      value={formData.contactDepartment}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={"Enter Department (e.g.Human Resources)"}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      // disabled={isRequestView}
                      error={
                        touched.contactDepartment && !!errors.contactDepartment
                      }
                      sx={getFieldStyles("contactDepartment")}
                    />
                    {renderFieldError("contactDepartment")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Designation
                    </Typography>
                    <TextField
                      fullWidth
                      name="contactDesignation"
                      value={formData.contactDesignation}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={"Enter Designation (e.g.Manager)"}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      // disabled={isRequestView}
                      error={
                        touched.contactDesignation &&
                        !!errors.contactDesignation
                      }
                      sx={getFieldStyles("contactDesignation")}
                    />
                    {renderFieldError("contactDesignation")}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  bgcolor: "#647490",
                  color: "white",
                  p: 2,
                  borderRadius: "8px",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                }}
              >
                Transport In-Charge Details (Optional)
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Full Name
                    </Typography>
                    <TextField
                      fullWidth
                      name="transportFullName" // Changed from transportName
                      value={formData.transportFullName || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter Full Name (e.g. John Doe)"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      error={
                        touched.transportFullName && !!errors.transportFullName
                      }
                      sx={getFieldStyles("transportFullName")}
                    />
                    {renderFieldError("transportFullName")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Email
                    </Typography>
                    <TextField
                      fullWidth
                      name="transportEmail"
                      value={formData.transportEmail || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter Email (e.g. transport@example.com)"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="email"
                      error={
                        touched.transportEmail &&
                        (!!errors.transportEmail ||
                          !!backendErrors.transportEmail)
                      }
                      sx={getFieldStyles("transportEmail")}
                    />
                    {renderFieldError("transportEmail")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Phone Number
                    </Typography>
                    <TextField
                      fullWidth
                      name="transportPhoneNumber"
                      value={formData.transportPhoneNumber || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter Phone Number (e.g. 9876543210)"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="tel"
                      error={
                        touched.transportPhoneNumber &&
                        (!!errors.transportPhoneNumber ||
                          !!backendErrors.transportPhoneNumber)
                      }
                      sx={getFieldStyles("transportPhoneNumber")}
                    />
                    {renderFieldError("transportPhoneNumber")}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Employee ID
                    </Typography>
                    <TextField
                      fullWidth
                      name="transportEmployeeId" // Changed from transportPhoneNumber
                      value={formData.transportEmployeeId || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter Employee ID (e.g. EMP5678)"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      error={
                        touched.transportEmployeeId &&
                        !!errors.transportEmployeeId
                      }
                      sx={getFieldStyles("transportEmployeeId")}
                    />
                    {renderFieldError("transportEmployeeId")}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            {/* Success Message */}
            {successMessage && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Typography
                  sx={{
                    color: "#28a745",
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "18px",
                  }}
                >
                  {successMessage}
                </Typography>
              </Box>
            )}
            {backendErrors.general && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Box
                  sx={{
                    backgroundColor: "#FDE7E7",
                    color: "#D32F2F",
                    padding: "14px 24px",
                    borderRadius: "12px",
                    border: "1px solid #F8C4C4",
                    maxWidth: "900px",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(238, 59, 43, 0.1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      fontSize: "18px",
                      lineHeight: "1.4",
                    }}
                  >
                    {backendErrors.general}
                  </Typography>
                </Box>
              </Box>
            )}
            {actionButtons}
          </Box>
        </Paper>
      </Box>

      {/* Rejection Modal */}
      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={confirmReject}
        isActionLoading={isActionLoading}
        organizationName={orgNameToReject}
      />
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: (theme) => theme.zIndex.snackbar + 10 }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
